// Phase 17 — Lifted Partner (Community Mentor) Dashboard
//
// This is the landing page for any user with the `lifted_partner` role.
// It surfaces:
//   • KPIs — active affiliates, pending partner/driver applications, unread DMs
//   • Affiliate roster with training % + last conversion
//   • Pending partner / driver review queues
//   • Training summary by role
//
// IMPORTANT
//   • This role is NOT an admin. NO billing / financial / system surfaces.
//   • All endpoints live under `/api/lifted-partner/...` which enforces the
//     role server-side.

import React, { useEffect, useState, useCallback } from "react";
import {
  Heart, Users, ClipboardList, Truck, GraduationCap, Bell, Loader2,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import LiftedSuppliesOverview from "@/components/lifted_supplies/LiftedSuppliesOverview";

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[0.7rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
        <Icon className="h-3.5 w-3.5 text-lifted-green" /> {label}
      </div>
      <div className="font-anton text-3xl text-white mt-1">{value ?? 0}</div>
    </div>
  );
}

function pct(n) {
  if (n == null) return "—";
  return `${n}%`;
}

export default function LiftedPartnerDashboard() {
  const [me, setMe] = useState(null);
  const [affs, setAffs] = useState(null);
  const [pendPartners, setPendPartners] = useState(null);
  const [pendDrivers, setPendDrivers] = useState(null);
  const [training, setTraining] = useState(null);
  const [err, setErr] = useState("");
  const [drawer, setDrawer] = useState(null); // selected affiliate

  const load = useCallback(async () => {
    try {
      const [m, a, pp, pd, t] = await Promise.all([
        api.get("/lifted-partner/me"),
        api.get("/lifted-partner/affiliates"),
        api.get("/lifted-partner/pending/partners"),
        api.get("/lifted-partner/pending/drivers"),
        api.get("/lifted-partner/training/summary"),
      ]);
      setMe(m.data);
      setAffs(a.data);
      setPendPartners(pp.data);
      setPendDrivers(pd.data);
      setTraining(t.data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="max-w-5xl mx-auto p-8 text-rose-300" data-testid="lp-error">
        {err}
      </div>
    );
  }
  if (!me) {
    return (
      <div className="max-w-5xl mx-auto p-8 text-lifted-dim flex items-center gap-2" data-testid="lp-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading mentor console…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lp-dashboard">
      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="text-lifted-green font-bebas tracking-[0.22em] text-[0.7rem] flex items-center gap-2">
            <Heart className="h-3.5 w-3.5" /> LIFTED PARTNER · COMMUNITY MENTOR
          </div>
          <h1 className="font-anton text-3xl sm:text-4xl uppercase text-white mt-1">
            Mentor & Onboarding Console
          </h1>
          <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
            Help affiliates progress, review incoming partner & driver requests, and keep an eye on
            training completion. You retain your existing affiliate compensation rules (50% / 10% / 3%).
          </p>
        </div>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="lp-kpis">
        <Kpi label="Active affiliates"   value={me.kpis.active_affiliates}  icon={Users}         testid="lp-kpi-affiliates" />
        <Kpi label="Pending partners"    value={me.kpis.pending_partners}   icon={ClipboardList} testid="lp-kpi-pending-partners" />
        <Kpi label="Pending drivers"     value={me.kpis.pending_drivers}    icon={Truck}         testid="lp-kpi-pending-drivers" />
        <Kpi label="Unread DMs"          value={me.kpis.unread_dms}         icon={Bell}          testid="lp-kpi-dms" />
      </section>

      {/* Phase 19 — Lifted Supplies overview (role-aware) */}
      <LiftedSuppliesOverview />

      {/* Affiliates roster */}
      <section className="glass-panel overflow-hidden" data-testid="lp-affiliates">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div className="font-bebas tracking-[0.22em] text-sm text-white flex items-center gap-2">
            <Users className="h-4 w-4 text-lifted-green" /> AFFILIATES YOU MENTOR
          </div>
          <div className="text-xs text-lifted-dim">{affs?.count || 0} total</div>
        </div>
        {!affs?.affiliates?.length ? (
          <div className="p-6 text-center text-sm text-lifted-dim">
            No real activity yet — once affiliates sign up here, they'll appear in this list.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
              <tr className="border-b border-white/5">
                <th className="px-4 py-2 text-left">Affiliate</th>
                <th className="px-4 py-2 text-left">Tier</th>
                <th className="px-4 py-2 text-left">Training</th>
                <th className="px-4 py-2 text-left">Referrals</th>
                <th className="px-4 py-2 text-left">Last conversion</th>
              </tr>
            </thead>
            <tbody>
              {affs.affiliates.map((a) => (
                <tr key={a.user_id} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                    onClick={() => setDrawer(a)}
                    data-testid={`lp-aff-${a.user_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-white underline-offset-2 hover:underline">{a.name}</div>
                    <div className="text-[0.7rem] text-lifted-dim">{a.email}</div>
                  </td>
                  <td className="px-4 py-2.5 capitalize">{(a.tier || "lift_off").replace("_", " ")}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-10 text-xs">{pct(a.training_completion_pct)}</span>
                      <div className="flex-1 max-w-[140px] h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className="h-full bg-lifted-green"
                          style={{ width: `${Math.min(100, a.training_completion_pct || 0)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">{a.lifetime_referrals || 0}</td>
                  <td className="px-4 py-2.5 text-xs text-lifted-dim">
                    {a.last_conversion_at ? a.last_conversion_at.slice(0, 16).replace("T", " ") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Pending queues + training summary */}
      <div className="grid lg:grid-cols-3 gap-4">
        <section className="glass-panel flex flex-col" data-testid="lp-pending-partners-card">
          <div className="px-4 py-3 border-b border-white/5 font-bebas tracking-[0.22em] text-sm text-white sticky top-0 bg-black/85 backdrop-blur-md z-10">
            PENDING PARTNER APPLICATIONS · {pendPartners?.count || 0}
          </div>
          {/* Phase 19 — desktop caps height with overflow scroll; mobile
              lets the page scroll instead so users can reach every row. */}
          <div className="p-4 lg:max-h-72 lg:overflow-y-auto" data-testid="lp-pending-partners-list">
            {!pendPartners?.partners?.length ? (
              <div className="text-sm text-lifted-dim">No pending partner applications.</div>
            ) : (
              <ul className="space-y-2">
                {pendPartners.partners.map((p) => (
                  <li key={p.id} className="border-l-2 border-amber-400/40 pl-3 py-1.5 text-sm"
                      data-testid={`lp-pp-${p.id}`}>
                    <div className="text-white">{p.name || p.email}</div>
                    <div className="text-xs text-lifted-dim">{p.email}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="glass-panel flex flex-col" data-testid="lp-pending-drivers-card">
          <div className="px-4 py-3 border-b border-white/5 font-bebas tracking-[0.22em] text-sm text-white sticky top-0 bg-black/85 backdrop-blur-md z-10">
            PENDING DRIVER APPLICATIONS · {pendDrivers?.count || 0}
          </div>
          <div className="p-4 lg:max-h-72 lg:overflow-y-auto" data-testid="lp-pending-drivers-list">
            {!pendDrivers?.drivers?.length ? (
              <div className="text-sm text-lifted-dim">No pending driver applications.</div>
            ) : (
              <ul className="space-y-2">
                {pendDrivers.drivers.map((d) => (
                  <li key={d.id || d.user_id} className="border-l-2 border-sky-400/40 pl-3 py-1.5 text-sm"
                      data-testid={`lp-pd-${d.id || d.user_id}`}>
                    <div className="text-white">{d.name || d.full_name || d.email || "Pending driver"}</div>
                    <div className="text-xs text-lifted-dim">{d.email || d.phone || ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="glass-panel" data-testid="lp-training-card">
          <div className="px-4 py-3 border-b border-white/5 font-bebas tracking-[0.22em] text-sm text-white flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-lifted-green" /> TRAINING PROGRESS
          </div>
          <div className="p-4 space-y-2">
            {!training?.by_role || Object.keys(training.by_role).length === 0 ? (
              <div className="text-sm text-lifted-dim">
                No training progress yet. Once affiliates, partners, drivers, or franchisees
                begin training, their progress will appear here.
              </div>
            ) : (
              Object.entries(training.by_role).map(([role, v]) => (
                <div key={role} className="flex items-center justify-between text-sm"
                     data-testid={`lp-train-${role}`}>
                  <span className="capitalize text-white">{role.replace("_", " ")}</span>
                  <span className="text-lifted-dim">
                    <span className="text-lifted-green">{v.active}</span> certified · {v.revoked} revoked
                  </span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Affiliate detail drawer */}
      {drawer && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="lp-aff-drawer"
          onClick={() => setDrawer(null)}
        >
          <div
            className="glass-panel max-w-lg w-full max-h-[85vh] overflow-y-auto p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[0.6rem] uppercase tracking-[0.22em] text-lifted-green">
                  Affiliate profile
                </div>
                <h3 className="font-anton text-2xl text-white mt-1">{drawer.name}</h3>
                <div className="text-xs text-lifted-dim">{drawer.email}</div>
                {drawer.phone && <div className="text-xs text-lifted-dim">{drawer.phone}</div>}
              </div>
              <button
                onClick={() => setDrawer(null)}
                className="text-white/50 hover:text-white"
                data-testid="lp-aff-drawer-close"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <DrawerField label="Tier" value={(drawer.tier || "lift_off").replace("_", " ")} />
              <DrawerField label="Training" value={pct(drawer.training_completion_pct)} />
              <DrawerField label="Active referrals" value={drawer.active_referrals ?? drawer.lifetime_referrals ?? 0} />
              <DrawerField label="Lifetime referrals" value={drawer.lifetime_referrals ?? 0} />
              <DrawerField label="Status" value={drawer.status || "active"} />
              <DrawerField
                label="Last conversion"
                value={drawer.last_conversion_at
                  ? drawer.last_conversion_at.slice(0, 10) : "—"}
              />
            </div>

            {drawer.social_handles && Object.keys(drawer.social_handles).length > 0 && (
              <div className="border-t border-white/5 pt-3">
                <div className="text-[0.6rem] uppercase tracking-[0.22em] text-lifted-dim mb-1">
                  Social handles
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(drawer.social_handles).map(([k, v]) =>
                    v ? (
                      <div key={k} className="text-white">
                        <span className="text-lifted-dim mr-1">{k}:</span>
                        <span className="font-mono">@{v}</span>
                      </div>
                    ) : null,
                  )}
                </div>
              </div>
            )}

            <p className="text-[0.65rem] text-lifted-dim border-t border-white/5 pt-3">
              Profile details respect privacy settings — only fields the affiliate has chosen
              to share are visible.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function DrawerField({ label, value }) {
  return (
    <div>
      <div className="text-[0.6rem] uppercase tracking-[0.22em] text-lifted-dim">{label}</div>
      <div className="text-white capitalize">{value}</div>
    </div>
  );
}
